import type { Ref } from "react";

import {
  Button,
  Column,
  FormField,
  Radio,
  RadioGroup,
  SegmentedControl,
  SegmentedControlOption,
  Text,
  TextInput,
} from "@hightouch/htui";

import { MapperPaneFooter } from "src/formkit/components/mapper-v2/mapper-list";
import type { MapperValue } from "src/formkit/components/mapper-v2/mapper-selection";
import {
  MappingType,
  STATIC_OPTIONS,
  StaticType,
} from "src/formkit/components/types";
import { NEW_STATIC_ICONS } from "src/formkit/components/utils";

const VALUE_TIPS = new Map<string, string>([
  [
    StaticType.STRING,
    "Any combination of letters, numbers, and special characters.",
  ],
  [StaticType.NUMBER, "Integers or decimals. Sent as a numeric type."],
]);

// Saved as typed, so long IDs keep every digit; these keys would let in exponent notation or a plus sign.
const BLOCKED_NUMBER_KEYS = new Set(["e", "E", "+"]);

type StaticPaneProps = {
  value: MapperValue;
  onChange: (value: MapperValue) => void;
  onApply: () => void;
  /** The value input, which takes focus when the popover opens. */
  inputRef: Ref<HTMLInputElement>;
};

/** A fixed value of a chosen type. */
export function StaticPane({
  value,
  onChange,
  onApply,
  inputRef,
}: StaticPaneProps) {
  const valueType = value.valueType || StaticType.STRING;
  const textValue = value.value == null ? "" : String(value.value);

  const changeType = (nextType: string) => {
    if (nextType === StaticType.NULL) {
      onChange({ type: MappingType.STATIC, value: null, valueType: nextType });
    } else if (nextType === StaticType.BOOLEAN) {
      onChange({ type: MappingType.STATIC, value: false, valueType: nextType });
    } else {
      onChange({ type: MappingType.STATIC, valueType: nextType });
    }
  };

  const renderValueInput = () => {
    switch (valueType) {
      case StaticType.NUMBER:
        return (
          <TextInput
            ref={inputRef}
            placeholder="Enter number"
            type="number"
            value={textValue}
            width="100%"
            onChange={(event) =>
              onChange({ ...value, value: event.target.value })
            }
            onKeyDown={(event) => {
              if (BLOCKED_NUMBER_KEYS.has(event.key)) event.preventDefault();
            }}
          />
        );
      case StaticType.BOOLEAN:
        return (
          <RadioGroup
            orientation="vertical"
            value={value.value === true ? "true" : "false"}
            onChange={(nextValue) =>
              onChange({ ...value, value: nextValue === "true" })
            }
          >
            <Radio label="True" value="true" />
            <Radio label="False" value="false" />
          </RadioGroup>
        );
      case StaticType.NULL:
        return (
          <Text color="text.secondary">
            A null value will be synced to this destination field.
          </Text>
        );
      default:
        return (
          <TextInput
            ref={inputRef}
            placeholder="Enter string"
            value={textValue}
            width="100%"
            onChange={(event) =>
              onChange({
                valueType: StaticType.STRING,
                ...value,
                value: event.target.value,
              })
            }
          />
        );
    }
  };

  return (
    <>
      <Column flex={1} gap={6} minHeight={0} overflowY="auto" p={4}>
        <FormField label="Type">
          <SegmentedControl
            aria-label="Type"
            value={valueType}
            width="100%"
            onChange={changeType}
          >
            {STATIC_OPTIONS.map(({ label, type }) => (
              <SegmentedControlOption
                key={type}
                icon={NEW_STATIC_ICONS[type]}
                label={label}
                value={type}
              />
            ))}
          </SegmentedControl>
        </FormField>
        <FormField label="Value" tip={VALUE_TIPS.get(valueType)}>
          {renderValueInput()}
        </FormField>
      </Column>
      <MapperPaneFooter>
        <Button variant="primary" onClick={onApply}>
          Apply value
        </Button>
      </MapperPaneFooter>
    </>
  );
}
